import { DetailQuery_moment_cards_ImageCard } from 'graphql/query/__generated__/DetailQuery';
import { getSmallerImage } from 'libs/utils/imageUtils/imageUtils';
import lgZoom from 'lightgallery/plugins/zoom';
import LightGallery from 'lightgallery/react';
import { useRef } from 'react';
import styled, { css } from 'styled-components/macro';

interface Props {
    card: DetailQuery_moment_cards_ImageCard;
    className?: string;
}

export const ImageCard = (props: Props): JSX.Element => {
    const length = props.card.images.length;
    const ref = useRef<HTMLImageElement>(null);

    function renderImagesFromTo(from: number, to: number) {
        if (to - from === 1) {
            const image = props.card.images[from];
            return (
                <S_SingleImage
                    src={getSmallerImage(image.imageUrl, 600)}
                    onClick={openLightGallery}
                />
            );
        } else {
            return props.card.images.slice(from, to).map((image) => {
                return (
                    <S_Image
                        $url={getSmallerImage(image.imageUrl, 600)}
                        key={image.imageUrl}
                        $length={to - from}
                        onClick={openLightGallery}
                    />
                );
            });
        }
    }

    function openLightGallery() {
        if (ref.current) {
            ref.current.click();
        }
    }

    return (
        <S_ImageCard className={props.className}>
            <LightGallery
                speed={500}
                plugins={[lgZoom]}
                showCloseIcon={true}
                preload={2}
                infiniteZoom={true}
                zoom={true}
            >
                {props.card.images.map((image) => {
                    return <S_Img ref={ref} src={image.fullImageUrl} key={image.fullImageUrl} />;
                })}
            </LightGallery>

            {length === 1 && (
                <S_Row style={{ flexDirection: 'column' }}>{renderImagesFromTo(0, 1)}</S_Row>
            )}
            {length === 2 && <S_Row>{renderImagesFromTo(0, 2)}</S_Row>}
            {length === 3 && (
                <S_Rows>
                    <S_Row>{renderImagesFromTo(0, 1)}</S_Row>
                    <S_Row>{renderImagesFromTo(1, 3)}</S_Row>
                </S_Rows>
            )}
            {length > 3 && (
                <S_Rows>
                    <S_Row>{renderImagesFromTo(0, 1)}</S_Row>
                    <S_Row>{renderImagesFromTo(1, length)}</S_Row>
                </S_Rows>
            )}
        </S_ImageCard>
    );
};

const S_ImageCard = styled.div`
    display: flex;
    flex-direction: column;
`;

const S_Rows = styled.div`
    display: flex;
    flex-direction: column;
`;

const S_Row = styled.div`
    display: flex;
    width: 100%;
    &:not(:last-child) {
        margin: 0 0 10px 0;
    }
`;

const S_SingleImage = styled.img`
    width: 100%;
    align-self: center;
`;

const S_Image = styled.div<{ $url: string; $length: number }>`
    background-color: #ecf0f5;
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center;
    background-image: url(${(props) => props.$url});
    width: 100%;
    ${(props) => {
        if (props.$length === 2) {
            return css`
                height: 200px;
                &:nth-child(1) {
                    margin: 0 5px 0 0;
                }
                &:nth-child(2) {
                    margin: 0 0px 0 5px;
                }
            `;
        } else if (props.$length >= 2) {
            return css`
                height: ${150 / props.$length + 50}px;
                margin: 0 5px;
                //first
                &:nth-child(1) {
                    margin: 0 5px 0 0;
                }
                &:nth-child(${props.$length}) {
                    margin: 0 0 0 5px;
                }
            `;
        }
    }}
`;

const S_Img = styled.img`
    display: none;
`;
